/**
 * Children CRUD + "current child" selection, shared across Home, Créer,
 * Réglages and the kid-mode entry point. The current child id is
 * remembered in localStorage so switching screens (or reloading) doesn't
 * reset the "for whom" context.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import * as ds from "./dataSource";
import type { Child, ChildCreateInput, ChildInput } from "./types";
import { useAuth } from "./auth";

const CURRENT_CHILD_KEY = "chouette.currentChildId";

interface ChildrenApi {
  children: Child[];
  loading: boolean;
  currentChild: Child | null;
  setCurrentChildId: (id: string) => void;
  refresh: () => Promise<void>;
  addChild: (input: ChildCreateInput) => Promise<Child>;
  updateChild: (id: string, input: Partial<ChildInput>) => Promise<void>;
  removeChild: (id: string) => Promise<void>;
}

const ChildrenCtx = createContext<ChildrenApi | null>(null);

export function ChildrenProvider({ children: reactChildren }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [list, setList] = useState<Child[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(() =>
    localStorage.getItem(CURRENT_CHILD_KEY),
  );

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      const value = await ds.listChildren();
      setList(value);
      if (value.length > 0 && !value.some((c) => c.id === currentId)) {
        setCurrentId(value[0].id);
        localStorage.setItem(CURRENT_CHILD_KEY, value[0].id);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setCurrentChildId = useCallback((id: string) => {
    setCurrentId(id);
    localStorage.setItem(CURRENT_CHILD_KEY, id);
  }, []);

  const addChild = useCallback(async (input: ChildCreateInput) => {
    const child = await ds.createChild(input);
    setList((prev) => [...prev, child]);
    setCurrentChildId(child.id);
    return child;
  }, [setCurrentChildId]);

  const updateChild = useCallback(async (id: string, input: Partial<ChildInput>) => {
    const updated = await ds.patchChild(id, input);
    setList((prev) => prev.map((c) => (c.id === id ? updated : c)));
  }, []);

  const removeChild = useCallback(async (id: string) => {
    await ds.deleteChild(id);
    setList((prev) => prev.filter((c) => c.id !== id));
    if (currentId === id) {
      setCurrentId(null);
      localStorage.removeItem(CURRENT_CHILD_KEY);
    }
  }, [currentId]);

  const currentChild = useMemo(
    () => list.find((c) => c.id === currentId) ?? list[0] ?? null,
    [list, currentId],
  );

  const api = useMemo<ChildrenApi>(
    () => ({ children: list, loading, currentChild, setCurrentChildId, refresh, addChild, updateChild, removeChild }),
    [list, loading, currentChild, setCurrentChildId, refresh, addChild, updateChild, removeChild],
  );

  return <ChildrenCtx.Provider value={api}>{reactChildren}</ChildrenCtx.Provider>;
}

export function useChildren(): ChildrenApi {
  const ctx = useContext(ChildrenCtx);
  if (!ctx) throw new Error("useChildren must be used inside ChildrenProvider");
  return ctx;
}
